import DialogContent from '@mui/material/DialogContent'
import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import { DataEditContextDateField } from 'Components/reusable/DataContext/fields/DataEditContextDateField'
import { DataEditContextDateResolutionField } from 'Components/reusable/DataContext/fields/DataEditContextDateResolutionField'
import { DataEditContextTranslationImport } from 'Components/reusable/DataContext/TranslationImport'
import Grid from '@mui/material/Grid2'
import { NewOrExisting } from 'Components/reusable/DataContext'
import React from 'react'
import { IEmployer, IPersonRole } from 'types/cvsInterfaces'
import newWithEditModal from 'Components/reusable/HOC/newWithEditModal'
import RoleCreate from '../ProjectsCard/RoleCreate'
import SkillsItem from '../SkillsCard/SkillsItem'
import { IRole } from 'types/levelItemInterfaces'
import AutoCalculatedInfo from 'Components/reusable/CaleoCustomComponents/AutoCalculatedInfo'
import { useTranslation } from 'react-i18next'

/** @notExported   */
interface IEmployerModalProps {
  /** Employer data. */
  data: IEmployer
  /** Employer data setter. */
  setData: (dataObject: IEmployer) => void
}

/**
 * Employer modal.
 *
 * @returns Employer modal component.
 * @notExported
 */
const EmployerModal: React.FC<IEmployerModalProps> = ({ data, setData }) => {
  const { t } = useTranslation()

  const getDisabledRoleOptions = () => {
    const disabledOptions: IRole[] = []
    data.EmployerRoles?.map(role => disabledOptions.push(role.Skill))
    return disabledOptions
  }

  const replaceOrAddRole = ({ newItem }: { newItem?: NewOrExisting<IPersonRole> }) => {
    setData({ ...data, EmployerRoles: [...(data.EmployerRoles ?? []), newItem as IPersonRole] })
  }

  const deleteItem = (id: number) => {
    const newItems: IPersonRole[] = (data.EmployerRoles ?? []).filter(item => item.skillId !== id)
    setData({ ...data, EmployerRoles: newItems })
  }

  const checkRoles = () => {
    const filtered = data.EmployerRoles?.filter(role => role.id !== undefined)
    const items = data.EmployerRoles

    return (
      <>
        <RoleCreate
          cvId={data.cvId}
          onAdd={newItem => {
            replaceOrAddRole(newItem)
          }}
          label="profile.employer.modal.role"
          usedRoles={getDisabledRoleOptions()}
          insetLabel
        />
        <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" style={{ marginTop: 10 }}>
          <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
            {filtered?.map(skill => (
              <SkillsItem
                key={skill.id}
                skill={skill}
                editable
                onDeleteClick={() => {
                  deleteItem(skill.skillId)
                }}
                type="role"
              />
            ))}
          </Grid>
          <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
            {items?.map(skill => {
              if (skill.id) return

              return (
                <SkillsItem
                  key={skill.skillId}
                  skill={skill}
                  editable
                  newChip={true}
                  onDeleteClick={() => {
                    deleteItem(skill.skillId)
                  }}
                  type="role"
                />
              )
            })}
          </Grid>
        </Grid>
      </>
    )
  }

  return (
    <DialogContent>
      <Grid container spacing={2}>
        <Grid
          size={{
            md: 4,
            sm: 6,
            xs: 12,
          }}
        >
          <DataEditContextTranslationImport<IEmployer> />
          <DataEditContextTextField<IEmployer> field="name" required fullWidth insetLabel />
          <Grid container spacing={2}>
            <Grid size={6}>
              <DataEditContextDateResolutionField<IEmployer> field="dateResolution" required />
            </Grid>
            <Grid size={6}>
              <DataEditContextDateField<IEmployer> field="startDate" required start={true} insetLabel />
              <DataEditContextDateField<IEmployer>
                field="endDate"
                isClearable
                end={true}
                minDate={data.startDate}
                insetLabel
              />
            </Grid>
            <DataEditContextTextField<IEmployer> field="primaryRole" fullWidth insetLabel />
          </Grid>
        </Grid>
        <Grid
          size={{
            md: 4,
            sm: 6,
            xs: 12,
          }}
        >
          <DataEditContextTextField<IEmployer>
            field="description"
            maxHintLength={1500}
            required
            multiline
            grow
            rows={11}
            fullWidth
            insetLabel
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            sm: 6,
            xs: 12,
          }}
        >
          <AutoCalculatedInfo text={t('profile.employer.modal.experienceAutoUpdateInfo')} variant="inline" />
          {checkRoles()}
        </Grid>
      </Grid>
    </DialogContent>
  )
}

export default newWithEditModal(EmployerModal)
